import { memo, useEffect, useState } from "react";
import { Container, Grid, IconButton, InputAdornment, TextField, Typography, Box, Button, CircularProgress, Alert, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { icon } from "@/app/style/icons";
import { validatePassword, validateName } from "@/utils/validateCredentials";
import { RouteTo } from "@routing/RouteMap";
import { USE_INVITE_LINK } from "@auth/graphql/mutations/useInviteLink";
import { FETCH_INVITE_LINK } from "@company/graphql/queries/fetchInviteLink";
import { VALIDATE_INVITE_LINK } from "@company/graphql/queries/validateInviteLink";
import { AuthPayload, Mutation, MutationUseInviteLinkArgs, Query } from "@/app/graphql/codegen";
import ConsentSection, { ConsentState } from "@auth/components/ConsentSection";
import { validateConsent } from "@utils/validateConsent";
import useAuthActions from "@auth/hooks/useAuthActions";
import client from "@/app/graphql/client";

interface FormErrors {
    name?: string;
    password?: string;
    confirmPassword?: string;
    submit?: string;
    agb?: string;
    datenschutz?: string;
}

const InviteRegistrationPage = memo(() => {
    const theme = useTheme();
    const { code } = useParams<{ code: string }>();
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const { login } = useAuthActions();

    const [consents, setConsents] = useState<ConsentState>({
        agbAccepted: false,
        datenschutzAccepted: false,
    });
    // Einladungslink validieren
    const {
        data: validationData,
        loading: validating,
        error: validationError,
    } = useQuery<Pick<Query, "validateInviteLink">>(VALIDATE_INVITE_LINK, { variables: { code }, fetchPolicy: "network-only" });

    // Details des Einladungslinks abrufen
    const { data: inviteLinkData, loading: inviteLinkLoading } = useQuery<Pick<Query, "inviteLink">>(FETCH_INVITE_LINK, {
        variables: { code },
        skip: !validationData?.validateInviteLink,
    });

    // Mutation zum Verwenden des Einladungslinks
    const [inviteLinkMutation, { loading: registering }] = useMutation<Pick<Mutation, "useInviteLink">, MutationUseInviteLinkArgs>(USE_INVITE_LINK, {
        onError: (error) => {
            console.error(error);
            setErrors((prev) => ({ ...prev, submit: "Registrierung fehlgeschlagen: " + error.message }));
        },
        onCompleted: async (data) => {
            const payload = data.useInviteLink as unknown as AuthPayload;
            if (payload?.authToken) {
                login(payload); // navigates to dashboard
                await client.resetStore();
            } else {
                setErrors((prev) => ({ ...prev, submit: "Registrierung fehlgeschlagen" }));
            }
        },
    });

    // Namen aus der E-Mail-Adresse des Einladungslinks vorab ausfüllen
    useEffect(() => {
        if (inviteLinkData?.inviteLink?.email) {
            // Name aus der E-Mail extrahieren (optionale Verbesserung)
            const emailName = inviteLinkData.inviteLink.email.split("@")[0];
            setName(emailName);
        }
    }, [inviteLinkData]);

    // Formular validieren
    const validateForm = (): boolean => {
        const errors: FormErrors = {};

        const nameError = validateName(name);
        if (nameError) errors.name = nameError;

        const passwordError = validatePassword(password);
        if (passwordError) errors.password = passwordError;

        if (password !== confirmPassword) {
            errors.confirmPassword = "Die Passwörter stimmen nicht überein";
        }

        // Validate consents
        const consentErrors = validateConsent(consents);
        if (consentErrors.agb) errors.agb = consentErrors.agb;
        if (consentErrors.datenschutz) errors.datenschutz = consentErrors.datenschutz;

        setErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle consent changes
    const handleConsentChange = (newConsents: ConsentState) => {
        setConsents(newConsents);
        // Clear consent errors when user makes changes
        setErrors((prev) => ({ 
            ...prev, 
            agb: undefined, 
            datenschutz: undefined 
        }));
    };

    // Registrierung durchführen
    const handleRegister = async () => {
        if (!validateForm() || !code) return;

        inviteLinkMutation({
            variables: {
                code,
                password,
                name,
            },
        });
    };

    // Enter-Taste abfangen
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            handleRegister();
        }
    };

    // Once mutation completes we'll navigate via login(); no extra redirect needed

    // Ladeanimation anzeigen
    if (validating || inviteLinkLoading) {
        return (
            <Container maxWidth="xs">
                <Box sx={{ mt: 8, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Überprüfe Einladungslink...</Typography>
                </Box>
            </Container>
        );
    }

    // Fehlermeldung anzeigen, wenn der Einladungslink ungültig ist
    if (validationError || !validationData?.validateInviteLink) {
        return (
            <Container maxWidth="xs">
                <Box sx={{ mt: 8 }}>
                    <Alert severity="error">Dieser Einladungslink ist ungültig oder wurde bereits verwendet.</Alert>
                    <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Button variant="contained" href="/login">
                            Zum Login
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    // Registrierungsformular anzeigen
    return (
        <Container maxWidth="xs">
            <Grid container spacing={3} justifyContent="center" sx={{ mt: 4 }}>
                <Grid item xs={12}>
                    <Typography variant="h2">Einladung annehmen</Typography>
                    {inviteLinkData?.inviteLink?.email && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            für {inviteLinkData.inviteLink.email}
                        </Typography>
                    )}
                    <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                        Willkommen! Du wurdest zum Firmenaccount von "{inviteLinkData?.inviteLink?.company?.name}" eingeladen!
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Name"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setErrors((prev) => ({ ...prev, name: undefined, submit: undefined }));
                        }}
                        error={!!errors.name}
                        helperText={errors.name}
                        disabled={registering}
                        onKeyDown={handleKeyDown}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body1" color="textSecondary">
                        Setze Dein neues Account-Passwort:
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Passwort"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setErrors((prev) => ({ ...prev, password: undefined, submit: undefined }));
                        }}
                        error={!!errors.password}
                        helperText={errors.password}
                        disabled={registering}
                        onKeyDown={handleKeyDown}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={registering}>
                                        {showPassword ? (
                                            <icon.hidePassword sx={{ fontSize: 20, color: "semantic.icon.functional" }} />
                                        ) : (
                                            <icon.showPassword sx={{ fontSize: 20, color: "semantic.icon.functional" }} />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Passwort bestätigen"
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            setErrors((prev) => ({ ...prev, confirmPassword: undefined, submit: undefined }));
                        }}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        disabled={registering}
                        onKeyDown={handleKeyDown}
                    />
                </Grid>

                <Grid item xs={12}>
                    <ConsentSection
                        consents={consents}
                        onConsentChange={handleConsentChange}
                        disabled={registering}
                        errors={{
                            agb: errors.agb,
                            datenschutz: errors.datenschutz
                        }}
                    />
                </Grid>

                {errors.submit && (
                    <Grid item xs={12}>
                        <Alert severity="error">{errors.submit}</Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Button variant="contained" fullWidth onClick={handleRegister} disabled={registering}>
                        {registering ? "Registrierung..." : "Registrierung abschließen"}
                    </Button>
                </Grid>
            </Grid>
        </Container>
    );
});

export default InviteRegistrationPage;
